import { derive, incidentTypeLabel, DATA_TYPES, ORG_TYPES, SYSTEMS, MAGNITUDES, US_STATES } from "./intake"
import { STEPS, stepItems } from "./steps"
import { resolveAllClocks, DEADLINES_REVIEWED } from "./deadlines"
import { csfFocus, csfResults } from "./csf"
import { reportDestinationsFor } from "./directory/refs"
import type { Workspace } from "./workspace"

const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString() : "—")
const labelOf = <T extends { id: string; label: string }>(arr: readonly T[], id: string) =>
  arr.find((a) => a.id === id)?.label ?? id

export const DISCLAIMER_SHORT =
  "General information only. Not legal, forensic, or insurance advice. Laws change and vary; confirm with qualified counsel and a qualified incident-response provider."

/** Hand-off summary: readable by the morning team, the customer, or counsel. */
export function toMarkdownSummary(w: Workspace, generatedAt = new Date()): string {
  const i = w.intake
  const d = derive(i)
  const lines: string[] = []
  lines.push(`# Incident hand-off summary${w.label ? ` — ${w.label}` : ""}`)
  lines.push("")
  lines.push(`Generated ${generatedAt.toLocaleString()} from afterabreach.com (based on NIST SP 800-61 Rev. 3). ${DISCLAIMER_SHORT}`)
  lines.push("")
  lines.push("## Situation")
  lines.push(`- Type: ${incidentTypeLabel(i.incidentType)}`)
  lines.push(`- Still active: ${i.stillActive}`)
  lines.push(`- First noticed: ${fmt(i.noticedAt)}`)
  lines.push(`- Confirmed / declared: ${fmt(i.confirmedAt)}`)
  lines.push(`- Severity (rough guide): ${d.severity}${d.severityReasons.length ? " — " + d.severityReasons.join(" ") : ""}`)
  lines.push(`- Systems: ${i.systems.map((s) => labelOf(SYSTEMS, s)).join("; ") || "—"}`)
  lines.push(`- Backups intact: ${i.backupsIntact}`)
  lines.push("")
  lines.push("## Data and people")
  lines.push(`- Data types: ${i.dataTypes.map((t) => labelOf(DATA_TYPES, t)).join("; ") || "—"}`)
  lines.push(`- Encrypted: ${i.encrypted}`)
  lines.push(`- People affected (estimate): ${labelOf(MAGNITUDES, i.magnitude)}`)
  lines.push(`- Affected people in US: ${i.affectedInUS}; outside US: ${i.affectedOutsideUS}`)
  const states = i.affectedStates.map((c) => US_STATES.find((s) => s.code === c)?.name ?? c)
  lines.push(`- States/territories: ${states.join(", ") || "—"}`)
  lines.push(`- Organization type: ${i.orgTypes.map((t) => labelOf(ORG_TYPES, t)).join("; ") || "—"}`)
  lines.push("")
  lines.push("## Regimes to ask counsel about (may apply — not a legal determination)")
  if (d.regimes.length === 0) lines.push("- None flagged from the answers given.")
  for (const r of d.regimes) lines.push(`- ${r.name} — ${r.why} (${r.url})`)
  if (d.statesNotCovered) lines.push("- State breach-notification laws also apply and are NOT covered by this tool yet.")
  if (d.nonUS) lines.push("- Non-US laws (e.g., GDPR) may apply and are NOT covered by this tool.")
  lines.push("")
  lines.push("## Deadline clocks (conservative estimates; confirm with counsel)")
  if (!DEADLINES_REVIEWED) lines.push("_Draft estimates: not yet reviewed by counsel. Penalties may apply for missed required notices._")
  const clocks = resolveAllClocks(i, w.clockOverrides)
  if (clocks.length === 0 && w.customClocks.length === 0) lines.push("- None flagged. State and non-US laws also apply and are not covered by this tool.")
  for (const c of clocks) {
    const when = c.date ? c.date.toLocaleString() : "no date yet"
    const tag = c.hidden ? " [marked not applicable by user]" : c.source === "lawyer-due" || c.source === "lawyer-start" ? " [set by user, e.g. per counsel]" : ""
    lines.push(`- ${c.obligation.regime} (${c.obligation.citation}): ${when}${tag}. Clock starts: ${c.obligation.clockStarts}${c.override?.note ? ` Note: ${c.override.note}` : ""}`)
    const where = reportDestinationsFor(c.obligation.id).slice(0, 3)
    if (where.length) lines.push(`  - Where to file: ${where.map((d) => `${d.name} (${d.urls[0]?.url ?? ""})`).join("; ")}`)
  }
  for (const c of w.customClocks) lines.push(`- ${c.label}: ${fmt(c.dueIso)} [custom]${c.note ? ` Note: ${c.note}` : ""}`)
  lines.push("")
  lines.push("## After the incident: NIST CSF focus")
  for (const f of csfFocus(i)) lines.push(`- ${f.category.name} (${f.category.id}): ${f.reasons.join(" ")}`)
  const csf = csfResults(w.csfCheck)
  if (csf.answered > 0) {
    lines.push("")
    lines.push("Self-check (rough, not an audit):")
    for (const r of csf.results) lines.push(`- ${r.fn.name}: ${r.answer ?? "not answered"}`)
  }
  lines.push("")
  lines.push("## Progress")
  for (const s of STEPS) {
    const items = stepItems(s, i)
    const done = items.filter((it) => w.checks[it.id]?.done).length
    lines.push(`### ${s.n}. ${s.title} (${done}/${items.length})`)
    for (const it of items) {
      const c = w.checks[it.id]
      lines.push(`- [${c?.done ? "x" : " "}] ${it.text}${c?.owner ? ` — owner: ${c.owner}` : ""}${c?.doneAt ? ` (done ${fmt(c.doneAt)})` : ""}`)
    }
  }
  lines.push("")
  lines.push("## Assignments")
  if (w.assignments.length === 0) lines.push("- (none)")
  for (const a of w.assignments) {
    const when = a.status === "done" && a.doneAt ? ` (done ${fmt(a.doneAt)})` : a.dueAt ? ` (due ${fmt(a.dueAt)})` : ""
    lines.push(`- [${a.status === "done" ? "x" : a.status === "cancelled" ? "-" : " "}] ${a.title} — ${a.assigneeName || "unassigned"}${when}${a.note ? `. ${a.note}` : ""}`)
  }
  lines.push("")
  lines.push("## Timeline")
  if (w.log.length === 0) lines.push("- (no entries)")
  for (const e of [...w.log].sort((a, b) => a.at.localeCompare(b.at))) lines.push(`- ${fmt(e.at)} — ${e.text}`)
  lines.push("")
  lines.push("## Evidence collected")
  if (w.evidence.length === 0) lines.push("- (none recorded)")
  for (const e of w.evidence) lines.push(`- ${e.what} — from ${e.from}; collected by ${e.collectedBy} at ${fmt(e.collectedAt)}; stored: ${e.location}`)
  lines.push("")
  lines.push("## Contacts")
  if (w.contacts.length === 0) lines.push("- (none recorded)")
  for (const c of w.contacts) lines.push(`- ${c.role}: ${c.name} ${c.phone}${c.notes ? " — " + c.notes : ""}`)
  lines.push("")
  return lines.join("\n")
}

export function toJson(w: Workspace): string {
  return JSON.stringify(w, null, 2)
}
